/**
 * NewTaskModal: minimal task creation form for the Director Command Center.
 *
 * SAKME-TASKSTAB-COCKPIT-FIX-1 Change 1, hardened by
 * BOARD-NEW-TASK-CATEGORY-TAXONOMY-1 + BOARD-NEW-TASK-FORM-RESET-UX-1.
 *
 * Fields:
 *   - task_id (required, unique)
 *   - title (required)
 *   - description (optional)
 *   - category (locked to the canonical taxonomy in `taskCategories.ts`,
 *               grouped by room; freeform "+ New category…" is gone — see
 *               TASK-CATEGORY-TAXONOMY.md Rule 4)
 *   - priority (critical | high | medium | low, default medium)
 *   - tier (0-9, default 2)
 *   - tags (comma-separated)
 *
 * Form state resets after a successful create AND on cancel/close, so
 * reopening the modal never carries stale fields from the previous attempt.
 *
 * Submission errors render inline in the form. The parent owns the
 * `creating` and `error` state and the close lifecycle.
 */

import { useState, useEffect, useRef } from 'react';
import { X, Loader2 } from 'lucide-react';
import type { TaskPriority } from '@/api/types';
import {
  TASK_CATEGORY_ROOMS,
  CANONICAL_CATEGORIES,
  isCanonicalCategory,
  DEFAULT_NEW_TASK_CATEGORY,
} from '@/lib/taskCategories';

interface NewTaskModalProps {
  open: boolean;
  creating: boolean;
  error: string | null;
  /**
   * Categories observed on existing tasks. Currently unused for selection
   * (the form is locked to the canonical taxonomy) but kept on the props
   * for future drift-detection / suggestion UX.
   */
  existingCategories: string[];
  defaultCategory?: string;
  onClose: () => void;
  onSubmit: (payload: {
    task_id: string;
    title: string;
    description: string;
    category: string;
    priority: TaskPriority;
    tier: number;
    tags: string[];
  }) => void;
}

function pickInitialCategory(defaultCategory?: string): string {
  if (defaultCategory && isCanonicalCategory(defaultCategory)) return defaultCategory;
  if (isCanonicalCategory(DEFAULT_NEW_TASK_CATEGORY)) return DEFAULT_NEW_TASK_CATEGORY;
  return CANONICAL_CATEGORIES[0];
}

export function NewTaskModal({
  open,
  creating,
  error,
  existingCategories: _existingCategories,
  defaultCategory,
  onClose,
  onSubmit,
}: NewTaskModalProps) {
  const [taskId, setTaskId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryChoice, setCategoryChoice] = useState<string>(
    () => pickInitialCategory(defaultCategory),
  );
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [tier, setTier] = useState<number>(2);
  const [tagsRaw, setTagsRaw] = useState('');

  // Reset whenever the modal transitions closed → open. We watch `open`
  // (not the category list) so stale category lists don't trigger a
  // mid-edit reset. After a successful create the parent flips `open`
  // false → true on the next click, which clears the form via this effect.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setTaskId('');
      setTitle('');
      setDescription('');
      setCategoryChoice(pickInitialCategory(defaultCategory));
      setPriority('medium');
      setTier(2);
      setTagsRaw('');
    }
    wasOpen.current = open;
  }, [open, defaultCategory]);

  if (!open) return null;

  const effectiveCategory = categoryChoice;

  const taskIdValid = taskId.trim().length > 0;
  const titleValid = title.trim().length > 0;
  const categoryValid = isCanonicalCategory(effectiveCategory);
  const formValid = taskIdValid && titleValid && categoryValid && !creating;

  const handleSubmit = () => {
    if (!formValid) return;
    const tags = tagsRaw
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    onSubmit({
      task_id: taskId.trim(),
      title: title.trim(),
      description: description.trim(),
      category: effectiveCategory,
      priority,
      tier,
      tags,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="new-task-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        className="bg-[#0d0d0d] border border-[#39FF14]/40 rounded-lg shadow-2xl w-[480px] max-w-[92vw] max-h-[88vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <h2 className="text-sm font-mono font-bold text-[#39FF14]">+ New Task</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-300 rounded hover:bg-gray-800"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form body */}
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-[10px] font-mono text-gray-500 uppercase mb-1">
              Task ID <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={taskId}
              onChange={(e) => setTaskId(e.target.value)}
              placeholder="e.g. DIR-ADMIN-1"
              autoFocus
              className="w-full bg-gray-900/50 border border-gray-700 rounded px-2 py-1.5 text-sm font-mono text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-[#39FF14]/50"
              data-testid="new-task-task-id"
            />
          </div>

          <div>
            <label className="block text-[10px] font-mono text-gray-500 uppercase mb-1">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="One-line summary"
              className="w-full bg-gray-900/50 border border-gray-700 rounded px-2 py-1.5 text-sm font-mono text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-[#39FF14]/50"
              data-testid="new-task-title"
            />
          </div>

          <div>
            <label className="block text-[10px] font-mono text-gray-500 uppercase mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional details, context, links"
              rows={3}
              className="w-full bg-gray-900/50 border border-gray-700 rounded px-2 py-1.5 text-xs font-mono text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-[#39FF14]/50 resize-y"
              data-testid="new-task-description"
            />
          </div>

          <div>
            <label className="block text-[10px] font-mono text-gray-500 uppercase mb-1">
              Category <span className="text-red-400">*</span>
            </label>
            <select
              value={categoryChoice}
              onChange={(e) => setCategoryChoice(e.target.value)}
              className="w-full bg-gray-900/50 border border-gray-700 rounded px-2 py-1.5 text-xs font-mono text-gray-200 focus:outline-none focus:border-[#39FF14]/50"
              data-testid="new-task-category-select"
            >
              {TASK_CATEGORY_ROOMS.map((room) => (
                <optgroup key={room.key} label={room.label}>
                  {room.categories.map((c) => (
                    <option key={c.value} value={c.value} title={c.description}>
                      {c.value}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <p className="mt-1 text-[10px] font-mono text-gray-500 italic">
              Locked to the canonical taxonomy. New categories require a Director ruling — see
              docs/TASK-LIFECYCLE.md.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono text-gray-500 uppercase mb-1">
                Priority
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as TaskPriority)}
                className="w-full bg-gray-900/50 border border-gray-700 rounded px-2 py-1.5 text-xs font-mono text-gray-200 focus:outline-none focus:border-[#39FF14]/50"
                data-testid="new-task-priority"
              >
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-mono text-gray-500 uppercase mb-1">
                Tier (0–9)
              </label>
              <input
                type="number"
                value={tier}
                min={0}
                max={9}
                onChange={(e) => setTier(Math.max(0, Math.min(9, Number(e.target.value) || 0)))}
                className="w-full bg-gray-900/50 border border-gray-700 rounded px-2 py-1.5 text-xs font-mono text-gray-200 focus:outline-none focus:border-[#39FF14]/50"
                data-testid="new-task-tier"
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-mono text-gray-500 uppercase mb-1">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={tagsRaw}
              onChange={(e) => setTagsRaw(e.target.value)}
              placeholder="e.g. backend, urgent, q2"
              className="w-full bg-gray-900/50 border border-gray-700 rounded px-2 py-1.5 text-xs font-mono text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-[#39FF14]/50"
              data-testid="new-task-tags"
            />
          </div>

          {error && (
            <div
              className="px-3 py-2 rounded border border-red-700/50 bg-red-900/20 text-xs font-mono text-red-300"
              data-testid="new-task-error"
            >
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-800">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-mono text-gray-400 hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!formValid}
            data-testid="new-task-submit"
            className="px-3 py-1.5 text-xs font-mono text-[#39FF14] border border-[#39FF14] rounded hover:bg-[#39FF14]/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {creating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Create Task
          </button>
        </div>
      </div>
    </div>
  );
}

